import React, { useEffect, useRef, useState } from "react"
import { API_BASE } from "../../../config/appConfig"
import { useAuth } from "../../../context/AuthContext"

import useAgendaData from "../hooks/useAgendaData"
import useAgendaActions from "../hooks/useAgendaActions"
import useAgendaDerivedMetrics from "../hooks/useAgendaDerivedMetrics"
import useNewAppointmentEngine from "../hooks/useNewAppointmentEngine"
import useDailyOpportunities from "../hooks/useDailyOpportunities"
import useBackendOpportunities from "../hooks/useBackendOpportunities"
import useAgendaSyncController from "../hooks/useAgendaSyncController"
import useAgendaUIState from "../hooks/useAgendaUIState"

import useAgendaNavigationContext from "../controllers/useAgendaNavigationContext"
import useNewAppointmentController from "../controllers/useNewAppointmentController"
import usePatientWorkspaceController from "../controllers/usePatientWorkspaceController"
import useAppointmentOpenController from "../controllers/useAppointmentOpenController"
import usePatientNavigationController from "../controllers/usePatientNavigationController"
import useOpportunityController from "../controllers/useOpportunityController"
import useAgendaCalendarController from "../controllers/useAgendaCalendarController"

import { useLocation, useNavigate } from "react-router-dom"
import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicSubscription } from "../../subscriptions/hooks/useClinicSubscription"
import {
  specialtyLabel,
  statusLabel,
  planItemStatusLabel,
  formatMoney,
  formatDateTimeLocal,
  formatDateTime,
  getTodayLocal
} from "../utils/agendaHelpers"

import AgendaLayout from "../layouts/AgendaLayout"

const AGENDA_VIEW_STORAGE_KEY = "odontoflow_agenda_calendar_view"

export default function AgendaContainer() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const {
    activeClinicId,
    clinicOptions,
    setActiveClinicId,
    loadingClinics
  } = useActiveClinic()

  const storedClinicId = localStorage.getItem("odontoflow_clinic_id") || ""

  const fallbackSingleClinicId =
    Array.isArray(clinicOptions) && clinicOptions.length === 1
      ? clinicOptions[0]?.value || clinicOptions[0]?.id || ""
      : ""

  const clinicId =
    activeClinicId === "ALL"
      ? "ALL"
      : (activeClinicId && activeClinicId !== "ALL" ? activeClinicId : "") ||
        (storedClinicId && storedClinicId !== "ALL" ? storedClinicId : "") ||
        fallbackSingleClinicId ||
        ""

  const [calendarView, setCalendarView] = useState(() => {
    if (typeof window === "undefined") return "timeGridDay"
    return localStorage.getItem(AGENDA_VIEW_STORAGE_KEY) || "timeGridDay"
  })

  const { subscription } = useClinicSubscription(clinicId)
  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason = subscription?.access?.reason || ""

  const [events, setEvents] = useState([])
  const [date, setDate] = useState(getTodayLocal())
  const [patients, setPatients] = useState([])
  const [patientQuery, setPatientQuery] = useState("")
  const [loadingPatients, setLoadingPatients] = useState(false)
  const [, setHeldSlots] = useState([])
  const [treatments, setTreatments] = useState([])
  const [doctors, setDoctors] = useState([])
  const [chairs, setChairs] = useState([])

  const ui = useAgendaUIState()

  const calendarRef = useRef(null)
  const lastCalendarDateRef = useRef("")
  const pendingOpenHandledRef = useRef(false)

  const cardStyle = {
    borderRadius: 18,
    padding: 18,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.06)"
  }

  const surfaces = [
    { value: "ALL", label: "Pieza completa" },
    { value: "O", label: "Oclusal" },
    { value: "M", label: "Mesial" },
    { value: "D", label: "Distal" },
    { value: "V", label: "Vestibular" },
    { value: "L", label: "Lingual / Palatina" }
  ]

  const conditions = [
    "Caries",
    "Obturación",
    "Endodoncia",
    "Corona",
    "Implante",
    "Ausente",
    "Fractura",
    "Desgaste",
    "Prótesis",
    "Control",
    "Otro"
  ]

  const odontoStatuses = [
    { value: "ACTIVE", label: "Pendiente / presente" },
    { value: "COMPLETED", label: "Realizado" }
  ]

  const selectedTreatment = treatments.find((t) => t.id === ui.newAppt.treatmentId)

  const {
    doctorSuggestion,
    alternatives,
    loadingSuggestion,
    suggestingDoctor,
    loadingAlternatives,
    setDoctorSuggestion,
    setAlternatives,
    suggestDoctor,
    loadAlternatives
  } = useNewAppointmentEngine({
    clinicId,
    newAppt: ui.newAppt,
    setNewAppt: ui.setNewAppt,
    selectedTreatment,
    doctors,
    events
  })

  const resetTreatmentOdontogramForm = () => {
    ui.setTreatmentOdontogramForm({
      tooth: null,
      surface: "ALL",
      condition: "Caries",
      status: "ACTIVE",
      price: "",
      priceTreatmentId: ""
    })
  }

  const {
    loadPatients,
    loadTreatments,
    loadDoctors,
    loadChairs,
    loadHeldSlots,
    loadAgenda,
    loadFinancialSummary,
    loadPatientTreatmentPlans,
    loadPatientSnapshot,
    loadPatientOdontogram
  } = useAgendaData({
    API_BASE,
    clinicId,
    date,
    patientQuery,
    newAppt: ui.newAppt,
    setPatients,
    setLoadingPatients,
    setNewAppt: ui.setNewAppt,
    setTreatments,
    setDoctors,
    setChairs,
    setHeldSlots,
    setEvents,
    setFinancialSummary: ui.setFinancialSummary,
    setLoadingFinancialSummary: ui.setLoadingFinancialSummary,
    setPaymentForm: ui.setPaymentForm,
    setReceivableForm: ui.setReceivableForm,
    setTreatmentPlans: ui.setTreatmentPlans,
    setLoadingTreatmentPlans: ui.setLoadingTreatmentPlans,
    setPatientSnapshot: ui.setPatientSnapshot,
    setLoadingPatientSnapshot: ui.setLoadingPatientSnapshot,
    setPatientOdontogram: ui.setPatientOdontogram,
    setLoadingPatientOdontogram: ui.setLoadingPatientOdontogram
  })

  const {
    createAppointment,
    holdAppointment,
    createQuickPatient,
    confirmHeld,
    cancelHeld,
    checkInSelectedAppointment,
    registerTreatmentForSelectedAppointment,
    deleteTreatmentFromSelectedAppointment,
    registerPaymentForSelectedAppointment,
    createReceivableForSelectedAppointment,
    cancelPaymentForSelectedAppointment,
    updateSelectedAppointmentColor,
    updateSelectedAppointment,
    cancelSelectedAppointment,
    deleteSelectedAppointment,
    handleEventDrop,
    handleEventResize,
    applyDragAlternative
  } = useAgendaActions({
    API_BASE,
    clinicId,
    date,
    setDate,
    selectedTreatment,
    selectedHeldEvent: ui.selectedHeldEvent,
    selectedAppointment: ui.selectedAppointment,
    selectedAppointmentTreatmentId: ui.selectedAppointmentTreatmentId,
    newAppt: ui.newAppt,
    quickPatient: ui.quickPatient,
    paymentForm: ui.paymentForm,
    receivableForm: ui.receivableForm,
    treatmentOdontogramForm: ui.treatmentOdontogramForm,
    dragConflictInfo: ui.dragConflictInfo,
    setSaving: ui.setSaving,
    setCreatingPatient: ui.setCreatingPatient,
    setCheckingIn: ui.setCheckingIn,
    setRegisteringTreatment: ui.setRegisteringTreatment,
    setRegisteringPayment: ui.setRegisteringPayment,
    setCreatingReceivable: ui.setCreatingReceivable,
    setShowNewModal: ui.setShowNewModal,
    setDoctorSuggestion,
    setAlternatives,
    setQuickPatient: ui.setQuickPatient,
    setPatientQuery,
    setLoadingDragAlternatives: ui.setLoadingDragAlternatives,
    setNewAppt: ui.setNewAppt,
    setSelectedHeldEvent: ui.setSelectedHeldEvent,
    setSelectedAppointment: ui.setSelectedAppointment,
    setDragConflictInfo: ui.setDragConflictInfo,
    setDragAlternatives: ui.setDragAlternatives,
    resetTreatmentOdontogramForm,
    loadPatients,
    loadHeldSlots,
    loadAgenda,
    loadFinancialSummary,
    loadPatientSnapshot,
    loadPatientOdontogram,
    isReadOnly,
    readOnlyReason,
    onPlanItemLinked: async (patientId) => {
      await loadPatientTreatmentPlans(patientId)
    }
  })

  const {
    dailyOpportunities,
    loadingDailyOpportunities
  } = useDailyOpportunities({
    API_BASE,
    clinicId,
    date
  })

  const {
    backendOpportunityItems,
    loadingBackendOpportunityItems
  } = useBackendOpportunities({
    API_BASE,
    clinicId,
    date
  })

  const {
    agendaStatsForView,
    actionItems,
    delayRiskItems,
    capacityItems,
    allOpportunityItems
  } = useAgendaDerivedMetrics({
    clinicId,
    date,
    events,
    treatmentPlans: ui.treatmentPlans,
    backendOpportunityItems,
    selectedAppointment: ui.selectedAppointment,
    viewMode: ui.viewMode
  })

  const doctorsCount = doctors.length
  const appointmentsCount = events.filter((e) => !String(e.id).startsWith("held-")).length

  const autoSuggestedComplexity =
    doctorsCount >= 2 || appointmentsCount >= 8 ? "PRO" : "SIMPLE"

  const isSimpleMode = ui.uiComplexity === "SIMPLE"
  const isProMode = ui.uiComplexity === "PRO"

  const {
    resetNewAppointmentState,
    openEmptyNewAppointment,
    handleDateClick,
    onTreatmentChange,
    scheduleFromPlanItem,
    openFromOpportunityGap
  } = useNewAppointmentController({
    clinicId,
    treatments,
    selectedAppointment: ui.selectedAppointment,
    formatDateTimeLocal,
    setNewAppt: ui.setNewAppt,
    setDoctorSuggestion,
    setAlternatives,
    setPatientQuery,
    setQuickPatient: ui.setQuickPatient,
    setShowNewModal: ui.setShowNewModal,
    setPatientWorkspaceOpen: ui.setPatientWorkspaceOpen,
    isReadOnly,
    readOnlyReason
  })

  const {
    createOdontogramEntry,
    handlePatientAlertAction,
    openPatientWorkspace
  } = usePatientWorkspaceController({
    API_BASE,
    clinicId,
    selectedAppointment: ui.selectedAppointment,
    patientSnapshot: ui.patientSnapshot,
    patientOdontogram: ui.patientOdontogram,
    odontogramForm: ui.odontogramForm,
    setCreatingOdontogramEntry: ui.setCreatingOdontogramEntry,
    setOdontogramForm: ui.setOdontogramForm,
    setPatientAlerts: ui.setPatientAlerts,
    setLoadingPatientAlerts: ui.setLoadingPatientAlerts,
    setPatientWorkspaceOpen: ui.setPatientWorkspaceOpen,
    setPatientWorkspaceTab: ui.setPatientWorkspaceTab,
    loadPatientSnapshot,
    loadPatientTreatmentPlans,
    loadPatientOdontogram
  })

  const {
    handleCalendarEventClick,
    openAppointmentFromActionPanel
  } = useAppointmentOpenController({
    clinicId,
    events,
    setSelectedHeldEvent: ui.setSelectedHeldEvent,
    setSelectedAppointment: ui.setSelectedAppointment,
    setSelectedAppointmentTreatmentId: ui.setSelectedAppointmentTreatmentId,
    setFinancialSummary: ui.setFinancialSummary,
    setPatientSnapshot: ui.setPatientSnapshot,
    setPatientOdontogram: ui.setPatientOdontogram,
    setPaymentForm: ui.setPaymentForm,
    setReceivableForm: ui.setReceivableForm,
    resetTreatmentOdontogramForm,
    loadFinancialSummary,
    loadPatientSnapshot,
    loadPatientTreatmentPlans
  })

  const {
    openPatientFromAgenda,
    openPatientFromActionPanel
  } = usePatientNavigationController({
    clinicId,
    navigate,
    setActiveClinicId
  })

  const {
    handleOpportunityAction,
    handleOpportunityPanelAction
  } = useOpportunityController({
    clinicId,
    openAppointmentFromActionPanel,
    openPatientFromActionPanel,
    openFromOpportunityGap
  })

  const {
    tryOpenPendingAppointment
  } = useAgendaNavigationContext({
    location,
    navigate,
    clinicId,
    treatments,
    calendarRef,
    setActiveClinicId,
    setDate,
    setNewAppt: ui.setNewAppt,
    setDoctorSuggestion,
    setAlternatives,
    setPatientQuery,
    setQuickPatient: ui.setQuickPatient,
    setShowNewModal: ui.setShowNewModal,
    setSelectedAppointment: ui.setSelectedAppointment,
    loadHeldSlots,
    loadAgenda,
    handleCalendarEventClick,
    getTodayLocal,
    formatDateTimeLocal
  })

  useAgendaSyncController({
    clinicId,
    date,
    patientQuery,
    showNewModal: ui.showNewModal,
    newAppt: ui.newAppt,
    patients,
    calendarRef,
    lastCalendarDateRef,
    setPatients,
    setTreatments,
    setDoctors,
    setHeldSlots,
    setEvents,
    loadPatients,
    loadTreatments,
    loadDoctors,
    loadHeldSlots,
    loadAgenda
  })

  useEffect(() => {
    if (!clinicId || clinicId === "ALL") {
      setChairs([])
      return
    }

    loadChairs(clinicId)
  }, [clinicId, loadChairs])

  const {
    handleDatesSet
  } = useAgendaCalendarController({
    lastCalendarDateRef,
    setDate
  })

  useEffect(() => {
    if (!events?.length) return
    if (pendingOpenHandledRef.current) return

    pendingOpenHandledRef.current = true
    tryOpenPendingAppointment(events)
  }, [events, tryOpenPendingAppointment])

  useEffect(() => {
    pendingOpenHandledRef.current = false
  }, [location.key, clinicId])

  useEffect(() => {
    if (!ui.uiComplexityAuto) return
    ui.setUiComplexity(autoSuggestedComplexity)
  }, [ui.uiComplexityAuto, autoSuggestedComplexity, ui])

  const uiHelpers = {
    specialtyLabel,
    statusLabel,
    planItemStatusLabel,
    formatMoney,
    formatDateTimeLocal,
    formatDateTime,
    cardStyle,
    surfaces,
    conditions,
    odontoStatuses
  }

  return (
    <AgendaLayout
      clinicId={clinicId}
      clinicOptions={clinicOptions}
      loadingClinics={loadingClinics}
      date={date}
      setDate={setDate}
      navigate={navigate}
      userId={user?.id || ""}
      apiBase={API_BASE}
      patientQuery={patientQuery}
      ui={ui}
      dataState={{
        events,
        patients,
        treatments,
        doctors,
        chairs,
        loadingPatients,
        dailyOpportunities,
        loadingDailyOpportunities,
        loadingBackendOpportunityItems
      }}
      engine={{
        doctorSuggestion,
        alternatives,
        loadingSuggestion,
        suggestingDoctor,
        loadingAlternatives,
        suggestDoctor,
        loadAlternatives,
        setDoctorSuggestion,
        setAlternatives
      }}
      actions={{
        createAppointment,
        holdAppointment,
        createQuickPatient,
        confirmHeld,
        cancelHeld,
        checkInSelectedAppointment,
        registerTreatmentForSelectedAppointment,
        deleteTreatmentFromSelectedAppointment,
        registerPaymentForSelectedAppointment,
        createReceivableForSelectedAppointment,
        cancelPaymentForSelectedAppointment,
        updateSelectedAppointmentColor,
        updateSelectedAppointment,
        cancelSelectedAppointment,
        deleteSelectedAppointment,
        handleEventDrop,
        handleEventResize,
        applyDragAlternative,
        openAppointmentFromActionPanel,
        openPatientFromAgenda,
        handleOpportunityAction,
        handleOpportunityPanelAction,
        openPatientWorkspace,
        createOdontogramEntry,
        handlePatientAlertAction,
        handleDateClick,
        onTreatmentChange,
        scheduleFromPlanItem,
        openEmptyNewAppointment,
        resetNewAppointmentState,
        loadPatientTreatmentPlans,
        loadPatientSnapshot
      }}
      metrics={{
        agendaStatsForView,
        actionItems,
        delayRiskItems,
        capacityItems,
        allOpportunityItems,
        autoSuggestedComplexity,
        isSimpleMode,
        isProMode
      }}
      calendarView={calendarView}
      setCalendarView={setCalendarView}
      calendarRef={calendarRef}
      calendarHandlers={{
        handleDatesSet,
        handleCalendarEventClick
      }}
      uiHelpers={uiHelpers}
      setPatientQuery={setPatientQuery}
      isReadOnly={isReadOnly}
      readOnlyReason={readOnlyReason}
    />
  )
}